"use client";

/**
 * Marketing `<Btn>` — hover-animated CTA button. Client component because
 * onMouseEnter / onMouseLeave state lives on the user's pointer.
 *
 * Variants mirror `shared.jsx`:
 *   - primary   : white background, navy text (highest-contrast CTA)
 *   - secondary : translucent white surface, ghost border
 *   - ghost     : transparent, muted text → text on hover
 *   - blue      : accent blue background (used for "Get started" type CTAs)
 *
 * Sizes: sm | md | lg map to padding + font-size pairs.
 *
 * Rendered as `<a>` when `href` is set, `<button>` otherwise. The
 * underlying element receives all spread props for accessibility extras
 * (aria-*, data-* etc.).
 */

import { type CSSProperties, type ReactNode, useState } from "react";
import { cn } from "@/lib/utils/cn";

type Variant = "primary" | "secondary" | "ghost" | "blue";
type Size = "sm" | "md" | "lg";

interface BtnProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  onClick?: () => void;
  href?: string;
  icon?: ReactNode;
  className?: string;
  style?: CSSProperties;
  "aria-label"?: string;
  /**
   * Optional PostHog `cta_click` instrumentation. When set, clicking
   * the button fires a `cta_click` event with `{ location, plan? }`
   * before the underlying onClick / navigation runs. Opt-in so we
   * don't pollute analytics with every marketing chrome button.
   */
  analyticsLocation?: string;
  /** Optional companion property — included alongside `location`. */
  analyticsPlan?: string;
}

const SIZE_MAP: Record<Size, { px: number; py: number; fs: number }> = {
  sm: { px: 14, py: 9, fs: 13 },
  md: { px: 18, py: 12, fs: 14 },
  lg: { px: 22, py: 15, fs: 15 },
};

function variantStyles(variant: Variant, hovered: boolean): CSSProperties {
  switch (variant) {
    case "primary":
      return {
        background: hovered ? "#ffffff" : "var(--color-text)",
        color: "var(--color-bg)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
      };
    case "secondary":
      return {
        background: hovered
          ? "rgba(255,255,255,0.10)"
          : "rgba(255,255,255,0.06)",
        color: "var(--color-text)",
        borderColor: hovered
          ? "rgba(255,255,255,0.18)"
          : "rgba(255,255,255,0.10)",
      };
    case "ghost":
      return {
        background: "transparent",
        color: hovered ? "var(--color-text)" : "var(--color-muted)",
      };
    case "blue":
      return {
        background: hovered ? "#5B9CFF" : "var(--color-blue)",
        color: "#ffffff",
      };
  }
}

export function Btn({
  children,
  variant = "primary",
  size = "md",
  onClick,
  href,
  icon,
  className,
  style,
  analyticsLocation,
  analyticsPlan,
  ...rest
}: BtnProps) {
  // Fire CTA tracking before the user-supplied onClick / navigation.
  // We inline this rather than wrapping the imports at the top so
  // SSR pre-renders that include `<Btn>` don't pull in the posthog
  // client until the user actually interacts (lazy via require).
  const fireCta = () => {
    if (!analyticsLocation) return;
    void import("@/lib/posthog").then((m) =>
      m.trackEvent("cta_click", {
        location: analyticsLocation,
        ...(analyticsPlan ? { plan: analyticsPlan } : {}),
      }),
    );
  };
  const [hovered, setHovered] = useState(false);
  const sz = SIZE_MAP[size];

  const baseStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: `${sz.py}px ${sz.px}px`,
    fontSize: sz.fs,
    fontWeight: 500,
    letterSpacing: "-0.005em",
    borderRadius: 8,
    transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
    cursor: "pointer",
    border: "1px solid transparent",
    whiteSpace: "nowrap",
    transform: hovered ? "translateY(-1px)" : "none",
    ...variantStyles(variant, hovered),
    ...style,
  };

  const sharedHandlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onFocus: () => setHovered(true),
    onBlur: () => setHovered(false),
  };

  if (href) {
    return (
      <a
        href={href}
        onClick={fireCta}
        className={cn(className)}
        style={baseStyle}
        {...sharedHandlers}
        {...rest}
      >
        {children}
        {icon ? <span className="inline-flex">{icon}</span> : null}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        fireCta();
        onClick?.();
      }}
      className={cn(className)}
      style={baseStyle}
      {...sharedHandlers}
      {...rest}
    >
      {children}
      {icon ? <span className="inline-flex">{icon}</span> : null}
    </button>
  );
}
